import React from "react";
import { Sparkles, ShieldCheck, TrendingUp, Coins, BarChart3, Scale, Gauge, Info } from "lucide-react";
import { fmtPercent, fmtRatio, safeFixed } from "@/lib/format";
import { useTranslation } from "@/lib/i18n";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

/**
 * Internal helper — classify a metric value into a tone bucket while
 * treating null/undefined/NaN explicitly as "neutral" (i.e. unknown)
 * instead of silently falling back to zero and rendering as "bad".
 *
 * Phase 6.AH null-truthfulness rule: missing fundamentals must never
 * be visually misclassified as bearish.
 *
 *   toneFor(0.05, [0.04, 0])      → "good"   (>= 0.04)
 *   toneFor(0.02, [0.04, 0])      → "neutral"
 *   toneFor(-0.01, [0.04, 0])     → "bad"
 *   toneFor(null, [0.04, 0])      → "neutral" (NOT bad)
 *   toneFor(undefined, [0.04, 0]) → "neutral"
 */
function toneFor(value, [goodGte, neutralGte], { invert = false } = {}) {
  if (value === null || value === undefined) return "neutral";
  const v = Number(value);
  if (!Number.isFinite(v)) return "neutral";
  if (invert) {
    // Lower is better (e.g. P/E vs sector, debt-to-equity).
    if (v <= goodGte) return "good";
    if (v <= neutralGte) return "neutral";
    return "bad";
  }
  if (v >= goodGte) return "good";
  if (v >= neutralGte) return "neutral";
  return "bad";
}

/**
 * Safe percent formatter that returns "—" when the underlying value
 * is missing rather than rendering a misleading "0.00%".
 */
function pctOrDash(value, digits = 1) {
  if (value === null || value === undefined) return "—";
  const v = Number(value);
  if (!Number.isFinite(v)) return "—";
  return fmtPercent(v * 100, digits);
}

/**
 * Safe ratio formatter that returns "—" for missing values rather
 * than the misleading "0.00".
 */
function ratioOrDash(value, digits = 2) {
  if (value === null || value === undefined) return "—";
  const v = Number(value);
  if (!Number.isFinite(v)) return "—";
  return fmtRatio(v, digits);
}

/**
 * KeyDriversPanel — "Based on cash flow, valuation, growth analysis" trust block.
 *
 * Each driver chip exposes a tap/hover tooltip with a beginner-friendly,
 * non-academic explanation so users learn the metric while reading.
 *
 * Why this matters: most retail investors recognise the labels (ROIC, P/E,
 * etc.) but can't articulate what they actually mean. Shipping concise
 * explanations inline turns the panel into a teaching surface and increases
 * trust in the score.
 */
export default function KeyDriversPanel({ valuation, metrics, className = "" }) {
  const { t } = useTranslation();
  if (!valuation) return null;
  const d = valuation.diagnostics || {};
  const fcf = d.fcf_quality || {};
  const prof = d.profitability || {};
  const rel = d.relative_valuation || {};
  const bs = d.balance_sheet || {};
  const inst = valuation.institutional || {};
  const instVal = inst.valuation || {};

  const drivers = [
    {
      icon: Coins,
      label: t("analyze.drivers.label.fcf_yield"),
      value: pctOrDash(fcf.fcf_yield, 2),
      tone: toneFor(fcf.fcf_yield, [0.04, 0]),
      tip: {
        title: t("analyze.drivers.tip.fcf_yield_title"),
        body: t("analyze.drivers.tip.fcf_yield_body"),
      },
    },
    {
      icon: BarChart3,
      label: t("analyze.drivers.label.cash_flow_quality"),
      value: ratioOrDash(fcf.fcf_to_net_income),
      tone: toneFor(fcf.fcf_to_net_income, [0.8, 0.5]),
      tip: {
        title: t("analyze.drivers.tip.cash_flow_quality_title"),
        body: t("analyze.drivers.tip.cash_flow_quality_body"),
      },
    },
    {
      icon: Sparkles,
      label: t("analyze.drivers.label.roic"),
      value: pctOrDash(prof.roic, 1),
      tone: toneFor(prof.roic, [0.15, 0.08]),
      tip: {
        title: t("analyze.drivers.tip.roic_title"),
        body: t("analyze.drivers.tip.roic_body"),
      },
    },
    {
      icon: TrendingUp,
      label: t("analyze.drivers.label.revenue_growth"),
      value: pctOrDash(d.revenue_growth?.revenue_growth_yoy, 1),
      tone: toneFor(d.revenue_growth?.revenue_growth_yoy, [0.15, 0.05]),
      tip: {
        title: t("analyze.drivers.tip.revenue_growth_title"),
        body: t("analyze.drivers.tip.revenue_growth_body"),
      },
    },
    {
      icon: Scale,
      label: t("analyze.drivers.label.pe_vs_sector"),
      value: Number.isFinite(Number(rel.pe_to_sector_ratio))
        ? `${safeFixed(rel.pe_to_sector_ratio, 2)}x`
        : "—",
      // Inverted: lower P/E vs sector is better.
      tone: toneFor(rel.pe_to_sector_ratio, [0.9, 1.2], { invert: true }),
      tip: {
        title: t("analyze.drivers.tip.pe_vs_sector_title"),
        body: t("analyze.drivers.tip.pe_vs_sector_body"),
      },
    },
    {
      icon: ShieldCheck,
      label: t("analyze.drivers.label.debt_to_equity"),
      value: ratioOrDash(bs.debt_to_equity),
      // Inverted: lower leverage is better.
      tone: toneFor(bs.debt_to_equity, [1.0, 2.0], { invert: true }),
      tip: {
        title: t("analyze.drivers.tip.debt_to_equity_title"),
        body: t("analyze.drivers.tip.debt_to_equity_body"),
      },
    },
  ];

  // Optional 7th tile: Fair value confidence.
  const fvc = instVal.fair_value_confidence;
  if (fvc) {
    drivers.push({
      icon: Gauge,
      label: t("analyze.drivers.label.fair_value_confidence"),
      value: t("analyze.status." + fvc.toLowerCase()) || fvc,
      tone: fvc === "High" ? "good" : fvc === "Low" ? "bad" : "neutral",
      tip: {
        title: t("analyze.drivers.tip.fair_value_confidence_title"),
        body: t("analyze.drivers.tip.fair_value_confidence_body"),
      },
    });
  }

  return (
    <TooltipProvider delayDuration={150}>
      <div className={`rounded-2xl border border-border/70 bg-card/60 p-5 ${className}`} data-testid="key-drivers-panel">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-[#3182CE]" />
          <div className="font-medium">{t("analyze.drivers.title")}</div>
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          {t("analyze.drivers.subtitle")}
          <span className="ml-1 text-muted-foreground/70">{t("analyze.drivers.tap_hint")}</span>
        </p>
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-2">
          {drivers.map((dr, i) => (
            <DriverChip key={i} {...dr} testId={`driver-chip-${i}`} />
          ))}
        </div>
      </div>
    </TooltipProvider>
  );
}

function DriverChip({ icon: Icon, label, value, tone, tip, testId }) {
  const colors = tone === "good"
    ? { fg: "#3FAE89", bg: "rgba(63,174,137,0.10)", border: "rgba(63,174,137,0.30)" }
    : tone === "bad"
      ? { fg: "#E5484D", bg: "rgba(229,72,77,0.10)", border: "rgba(229,72,77,0.28)" }
      : { fg: "#E0B341", bg: "rgba(224,179,65,0.10)", border: "rgba(224,179,65,0.30)" };
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          data-testid={testId}
          className="group w-full text-left rounded-xl px-3 py-3 border bg-card/40 flex items-start gap-3 min-h-[68px] hover:bg-card/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--app-glow-blue)] transition-colors"
          style={{ borderColor: colors.border }}
          aria-label={`${label}: ${value}. Tap to learn more.`}
        >
          <span
            className="inline-flex w-7 h-7 items-center justify-center rounded-md shrink-0 mt-0.5"
            style={{ background: colors.bg, color: colors.fg }}
          >
            <Icon className="w-4 h-4" />
          </span>
          <div className="min-w-0 flex-1">
            {/* Label: sentence-case, higher contrast, allowed to wrap to 2 lines */}
            <div className="text-[11.5px] leading-snug text-foreground/75 flex items-start gap-1">
              <span className="break-words">{label}</span>
              <Info className="w-3 h-3 opacity-50 group-hover:opacity-100 transition-opacity shrink-0 mt-[2px]" aria-hidden />
            </div>
            <div className="font-mono tabular text-[15px] font-medium mt-0.5" style={{ color: colors.fg }}>
              {value}
            </div>
          </div>
        </button>
      </TooltipTrigger>
      <TooltipContent
        side="top"
        align="center"
        sideOffset={8}
        collisionPadding={12}
        avoidCollisions
        className="max-w-[320px] text-[12px] leading-[1.55] bg-popover/95 backdrop-blur-xl border border-border/70 shadow-[var(--app-shadow-elev-2)] z-50"
      >
        <div className="font-medium text-foreground mb-1">{tip.title}</div>
        <div
          className="text-muted-foreground/95"
          dangerouslySetInnerHTML={{ __html: tip.body }}
        />
      </TooltipContent>
    </Tooltip>
  );
}
